import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useEquipment } from '@hooks/useEquipment';
import { selectParsedEquipment } from '@utils/equipmentMapper';
import { EquipmentCard } from '@components/ProductOffers/configuration/cards';
import type { EquipmentSelectionStepProps } from '@components/ProductOffers/configuration/steps/EquipmentSelectionStep/EquipmentSelectionStep.types';
import { useEquipmentSelection } from '@components/ProductOffers/configuration/steps/EquipmentSelectionStep/useEquipmentSelection';
import {
  CloseButton,
  ErrorBanner,
} from '@components/ProductOffers/configuration/steps/shared.styles';
import { useShallow } from 'zustand/react/shallow';
import { useConfigurationStore } from '@store/configurationStore';
import type { PlanCategory } from '@t/configuration.types';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';

const TITLES: Record<PlanCategory, string> = {
  Internet: 'Select Your Internet Equipment',
  Video: 'Select Your TV Equipment',
  Phone: 'Select Your Phone Equipment',
  EV: '',
  Bundle: '',
};

const EDIT_TITLES: Record<PlanCategory, string> = {
  Internet: 'Edit Your Internet Equipment',
  Video: 'Edit Your TV Equipment',
  Phone: 'Edit Your Phone Equipment',
  EV: '',
  Bundle: '',
};

const SUBHEADERS: Record<PlanCategory, string> = {
  Internet: 'Choose Your Modem',
  Video: 'Choose Your Set Top Box',
  Phone: 'Choose Your Equipment',
  EV: '',
  Bundle: '',
};

export const EquipmentSelectionStep = ({ onClose, onConfirm, confirmLabel, loading }: EquipmentSelectionStepProps) => {
  const { activePlanConfig, editMode } = useConfigurationStore(
    useShallow((s) => ({
      activePlanConfig: s.activePlanConfig,
      editMode: s.editMode,
    }))
  );
  const theme = useTheme();
  const { selectedEquipment, showError, handleSelect, handleQuantityChange, handleConfirm } =
    useEquipmentSelection(onConfirm);

  const planCategory = activePlanConfig?.planCategory ?? 'Internet';
  const homeId = activePlanConfig?.homeIdOfQualifiedOffers ?? '';
  const planId = activePlanConfig?.planId ?? '';
  const sessionId = activePlanConfig?.sessionId ?? '';

  const { equipment: equipments, isLoading } = useEquipment(sessionId, homeId, planId, selectParsedEquipment);

  return (
    <>
      <DialogTitle>
        <Typography variant="title.step" className="StepHeader-title">
          {editMode ? EDIT_TITLES[planCategory] : TITLES[planCategory]}
        </Typography>
        <CloseButton onClick={onClose}>
          <CloseIcon variant="dialogCloseIcon" />
        </CloseButton>
      </DialogTitle>

      <DialogContent dividers={false}>
        <Stack spacing={1} useFlexGap>
          <Typography variant="heading.subsection">{SUBHEADERS[planCategory]}</Typography>
        </Stack>

        {showError && (
          <ErrorBanner>
            <ErrorOutlineIcon variant="errorBannerIcon" />
            <Typography variant="label.field" color={theme.novus.errorRed}>
              Selecting equipment is required
            </Typography>
          </ErrorBanner>
        )}

        {isLoading ? (
          <Typography variant="body.md" color={theme.novus.borderBlack}>
            Loading equipment options...
          </Typography>
        ) : (
          <Stack spacing={1.5} useFlexGap>
            {equipments.map((equipment) => {
              const isSelected = selectedEquipment?.id === equipment.id;
              const quantity = isSelected && selectedEquipment ? selectedEquipment.quantity : 1;
              return (
                <EquipmentCard
                  key={equipment.id}
                  equipment={equipment}
                  isSelected={isSelected}
                  quantity={quantity}
                  onSelect={() => handleSelect(equipment)}
                  onQuantityChange={(qty) => handleQuantityChange(equipment.id, qty)}
                />
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions className="footer-end">
        <LoadingButton variant="contained" onClick={handleConfirm} loading={loading}>
          <Typography variant="label.button" color="inherit">
            {confirmLabel ?? 'Confirm Equipment'}
          </Typography>
        </LoadingButton>
      </DialogActions>
    </>
  );
};
